import React from "react";
import { Stack, Typography, TextField, MenuItem, Box } from "@mui/material";
import { ArrowRight } from "lucide-react";

const hours = Array.from({ length: 12 }, (_, i) =>
  String(i + 1).padStart(2, "0")
);
const minutes = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, "0")
);

function convertMinutesToHM(min: number) {
  let h = Math.floor(min / 60);
  const m = String(min % 60).padStart(2, "0");
  const ap = h >= 12 ? "PM" : "AM";

  h = h % 12;
  if (h === 0) h = 12;

  return {
    h: String(h).padStart(2, "0"),
    m,
    ap,
  };
}

function convertHMToMinutes(h: string, m: string, ap: string) {
  let hour = parseInt(h, 10);
  const minute = parseInt(m, 10);

  if (ap === "PM" && hour !== 12) hour += 12;
  if (ap === "AM" && hour === 12) hour = 0;

  return hour * 60 + minute;
}

export default function CustomTimePicker({
  timeRange,
  setTimeRange,
}: {
  timeRange: number[];
  setTimeRange: (value: number[]) => void;
}) {
  const [startMin, endMin] = timeRange;

  const start = convertMinutesToHM(startMin);
  const end = convertMinutesToHM(endMin);

  const updateStart = (field: string, value: string) => {
    const newStart = convertHMToMinutes(
      field === "h" ? value : start.h,
      field === "m" ? value : start.m,
      field === "ap" ? value : start.ap
    );
    setTimeRange([newStart, endMin]);
  };

  const updateEnd = (field: string, value: string) => {
    const newEnd = convertHMToMinutes(
      field === "h" ? value : end.h,
      field === "m" ? value : end.m,
      field === "ap" ? value : end.ap
    );
    setTimeRange([startMin, newEnd]);
  };

  return (
    <Box p={1}>
      {/* <Typography className="tp-label">Select time:</Typography> */}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap={"wrap"}
        gap={1}
      >
        {/* ---------- START TIME ---------- */}
        <Stack>
          <Typography fontWeight={500} fontSize={13}>
            Start
          </Typography>

          <Box className="tp-box">
            <TextField
              select
              value={start.h}
              size="small"
              onChange={(e) => updateStart("h", e.target.value)}
              className="tp-input"
            >
              {hours.map((h) => (
                <MenuItem key={h} value={h}>
                  {h}
                </MenuItem>
              ))}
            </TextField>

            <Typography className="tp-colon">:</Typography>

            <TextField
              select
              value={start.m}
              size="small"
              variant="outlined"
              onChange={(e) => updateStart("m", e.target.value)}
              className="tp-input"
            >
              {minutes.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>

            <Box className="tp-ampm">
              <div
                className={`tp-ampm-btn ${start.ap === "AM" ? "active" : ""}`}
                onClick={() => updateStart("ap", "AM")}
              >
                AM
              </div>
              <div
                className={`tp-ampm-btn ${start.ap === "PM" ? "active" : ""}`}
                onClick={() => updateStart("ap", "PM")}
              >
                PM
              </div>
            </Box>
          </Box>
        </Stack>

        <Typography pt={2}>
          <ArrowRight size={18} />
        </Typography>

        {/* ---------- END TIME ---------- */}
        <Stack>
          <Typography fontWeight={500} fontSize={13}>
            End
          </Typography>

          <Box className="tp-box">
            <TextField
              select
              value={end.h}
              size="small"
              onChange={(e) => updateEnd("h", e.target.value)}
              className="tp-input"
            >
              {hours.map((h) => (
                <MenuItem key={h} value={h}>
                  {h}
                </MenuItem>
              ))}
            </TextField>

            <Typography className="tp-colon">:</Typography>

            <TextField
              select
              value={end.m}
              size="small"
              onChange={(e) => updateEnd("m", e.target.value)}
              className="tp-input"
            >
              {minutes.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>

            <Box className="tp-ampm">
              <div
                className={`tp-ampm-btn ${end.ap === "AM" ? "active" : ""}`}
                onClick={() => updateEnd("ap", "AM")}
              >
                AM
              </div>
              <div
                className={`tp-ampm-btn ${end.ap === "PM" ? "active" : ""}`}
                onClick={() => updateEnd("ap", "PM")}
              >
                PM
              </div>
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}
